import classnames from 'classnames';
import { find, isNil, isNumber, isObject, isString, noop, reduce, some } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import Dropdown, { Creatable } from 'react-select-plus';
import { toRequiredLabel } from 'utils';
import Tooltip from '../Tooltip';
import Loading from 'components/Loading';
import styles from './select.module.scss';

const MAX_OPTIONS_LENGTH_FOR_SEARCH = 20;
const DELETE_KEY_CODE = 46;
const BACKSPACE_KEY_CODE = 8;

export default class Select extends PureComponent {
  static propTypes = {
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    style: PropTypes.object,
    options: PropTypes.array.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    isValid: PropTypes.bool,
    isLoading: PropTypes.bool,
    validationMessage: PropTypes.string,
    label: PropTypes.string,
    position: PropTypes.string,
    noResultsMessage: PropTypes.string,
    isFull: PropTypes.bool,
    withoutHelpWrapper: PropTypes.bool,
    isRequired: PropTypes.bool,
    id: PropTypes.string,
    children: PropTypes.node,
    autosize: PropTypes.bool,
    /**
     * TODO: этот параметр рисует крестик и больше ничего, его надо бы удалить
     */
    clearable: PropTypes.bool,
    searchable: PropTypes.bool,
    noResultsText: PropTypes.string,
    ignoreDeleteEvent: PropTypes.bool,
    wrapperClass: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    tooltipTitle: PropTypes.any,
    tooltipContent: PropTypes.any,
    maxOptionsLength: PropTypes.number,
    optionRenderer: PropTypes.func,
    valueRenderer: PropTypes.func,
    filterOptions: PropTypes.func,
    creatable: PropTypes.bool,
    multi: PropTypes.bool,
    clearAllText: PropTypes.string,
  };

  static defaultProps = {
    options: [],
    placeholder: 'Выберите',
    noResultsMessage: 'Не найдено',
    isFull: false,
    withoutHelpWrapper: false,
    position: 'part',
    clearable: false,
    searchable: false,
    noResultsText: 'Ничего не найдено',
    ignoreDeleteEvent: false,
    maxOptionsLength: MAX_OPTIONS_LENGTH_FOR_SEARCH,
    wrapperClass: '',
    multi: false,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextValue = Select.getValue(nextProps.value, nextProps.options);

    if (nextValue !== prevState.currValue && !prevState.isDeleted) {
      return {
        currValue: nextValue,
        lastValue: nextValue,
      };
    }

    return null;
  }

  static getValue(value, options) {
    if (value && !isObject(value)) {
      return find(options, (o) => `${o.value}` === `${value}`);
    } else {
      return value;
    }
  }

  state = {
    currValue: null,
    lastValue: null,
    isDeleted: false,
    isOpened: false,
  };

  validate() {
    const { isValid, validationMessage } = this.props;
    let message = null;

    if (validationMessage) {
      message = (
        <div className={classnames('form_error', { green: isValid, red: !isValid })}>
          {validationMessage}
        </div>
      );
    }

    return message;
  }

  getOptionsLength = () => {
    const { options } = this.props;

    return reduce(
      options,
      (sum, option) => {
        if (option.options && option.options.length) {
          return sum + option.options.length;
        } else {
          return sum + 1;
        }
      },
      0,
    );
  };

  handleOpen = () => {
    this.setState({
      isOpened: true,
    });
  };

  handleChange = (value) => {
    const { options, onChange } = this.props;

    this.setState(
      {
        currValue: value,
      },
      () => {
        if (isNil(value)) {
          if (some(options, isObject)) {
            onChange({ value: null });
          } else {
            onChange(null);
          }
        } else {
          onChange(value);
        }
      },
    );
  };

  handleClose = () => {
    const { onChange } = this.props;
    const { isDeleted, lastValue } = this.state;

    if (isDeleted) {
      this.setState(
        {
          isDeleted: false,
        },
        () => {
          if (!isNil(lastValue)) {
            onChange(lastValue);
          }
        },
      );
    }

    this.setState({
      isOpened: false,
    });
  };

  isSearchable = () => {
    let { searchable } = this.props;

    if (!searchable) {
      const optionsLength = this.getOptionsLength();

      searchable = optionsLength > this.props.maxOptionsLength;
    }

    return searchable;
  };

  handleInputKeyDown = (event) => {
    const searchable = this.isSearchable();
    const keyCode = event.keyCode;
    const isInvalidEvent =
      (keyCode === DELETE_KEY_CODE || keyCode === BACKSPACE_KEY_CODE) &&
      (!searchable || !this.state.isOpened);

    if (isInvalidEvent) {
      event.preventDefault();
    }
  };

  normalizeOptions = () => {
    const { options } = this.props;

    return options.map((option) => {
      // library works with objects only
      if (isString(option) || isNumber(option)) {
        return {
          value: option,
          label: option,
        };
      }

      return option;
    });
  };

  shouldKeyDownEventCreateNewOption = ({ keyCode }) => {
    switch (keyCode) {
      case 9: // TAB
      case 13: // ENTER
        return true;
      default:
        return false;
    }
  };

  renderSelectWrapper() {
    const { label, isRequired, position, wrapperClass, validationMessage, isValid } = this.props;
    const labelClassess = classnames('form_label', {
      invalid: validationMessage && !isValid,
    });

    return (
      <div className={`form_item form_item__${position} ${wrapperClass}`}>
        {label ? (
          <div className={labelClassess}>{isRequired ? toRequiredLabel(label) : label}</div>
        ) : null}
        {this.renderSelect()}
        {this.validate()}
      </div>
    );
  }

  renderBaseSelect(selectClass) {
    const {
      style,
      id,
      children,
      clearable,
      noResultsText,
      noResultsMessage,
      filterOptions,
      creatable,
      optionRenderer,
      valueRenderer,
      autosize,
      multi,
      isLoading,
      clearAllText,
    } = this.props;
    const { isOpened, currValue } = this.state;

    const searchable = this.isSearchable();
    const options = this.normalizeOptions();
    const placeholder = options.length ? this.props.placeholder : noResultsMessage;
    const isDisabled = this.props.isDisabled || !options.length || isLoading;

    return (
      <div id={id} className={classnames([styles.select, selectClass])} style={style}>
        <div className="form-select__relative">
          {creatable ? (
            <Creatable
              promptTextCreator={(label) => `Добавить ${label}`}
              disabled={isDisabled}
              options={options}
              onFocus={noop}
              placeholder={searchable && isOpened ? null : placeholder}
              onChange={this.handleChange}
              value={currValue}
              clearable={clearable}
              searchable={searchable}
              noResultsText={noResultsText}
              onClose={this.handleClose}
              onOpen={this.handleOpen}
              onInputKeyDown={this.handleInputKeyDown}
              optionRenderer={optionRenderer}
              valueRenderer={valueRenderer}
              filterOptions={filterOptions}
              autosize={autosize}
              shouldKeyDownEventCreateNewOption={this.shouldKeyDownEventCreateNewOption}
              multi={multi}
              clearAllText={clearAllText}
            />
          ) : (
            <Dropdown
              promptTextCreator={(label) => `Добавить ${label}`}
              disabled={isDisabled}
              options={options}
              onFocus={noop}
              placeholder={searchable && isOpened ? null : placeholder}
              onChange={this.handleChange}
              value={currValue}
              clearable={clearable}
              searchable={searchable}
              noResultsText={noResultsText}
              onClose={this.handleClose}
              onOpen={this.handleOpen}
              onInputKeyDown={this.handleInputKeyDown}
              optionRenderer={optionRenderer}
              valueRenderer={valueRenderer}
              filterOptions={filterOptions}
              multi={multi}
              clearAllText={clearAllText}
            />
          )}
          {isLoading && <Loading />}
        </div>

        {children}

        {this.renderTooltip()}
      </div>
    );
  }

  renderTooltip = () => {
    const { tooltipContent, tooltipTitle = null } = this.props;

    return tooltipContent ? (
      <Tooltip
        content={tooltipContent}
        title={tooltipTitle}
        show={this.state.showTooltip}
        onShow={() => this.setShowTooltip(true)}
        onClose={() => this.setShowTooltip(false)}
      />
    ) : null;
  };

  renderSelect() {
    const {
      className,
      isFull,
      withoutHelpWrapper,
      isValid,
      validationMessage,
      tooltipTitle,
      tooltipContent,
    } = this.props;
    const selectClass = className
      ? className
      : classnames('form-select', {
          'form-select__full': isFull,
          'form-select__disable': this.props.isDisabled,
          'form-select__error': validationMessage && !isValid,
        });
    const helpWrapClass = (tooltipTitle || tooltipContent) && 'help-wrap';
    return isFull && !withoutHelpWrapper ? (
      <div className={helpWrapClass}>{this.renderBaseSelect(selectClass)}</div>
    ) : (
      this.renderBaseSelect(selectClass)
    );
  }

  render() {
    const { label, validationMessage } = this.props;
    return label || validationMessage ? this.renderSelectWrapper() : this.renderSelect();
  }
}
